/**
 * 当前订阅（套餐档次 + 到期时间）。
 *
 * 为什么是模块级单例：`AppHeader`（档位徽章、到期横幅）与额度页、支付页是**兄弟**关系，
 * 与 `auth-store.ts` 的处境完全相同——用 provide/inject 就得让每个入口自己记得 provide 一次，
 * 漏一处就是"头部说没有订阅、额度页说到期时间在下个月"。模块单例没有这个失配面。
 *
 * 为什么**不**把 subscription 并进 `/auth/me`：那属于契约变更（要升版本并同步三端），
 * 收益只是省掉一个请求；而 `GET /subscription` 本来就在契约 §3.5 里，支付成功时
 * `PaymentResultResponse` 还会顺带把它带回来。
 *
 * 本文件只做"拿到结果 → 写状态"，不做任何路由或"要不要登录"的判断。
 */

import { reactive } from 'vue';

import { SubscriptionResponseSchema, type Subscription } from '@spirit/contracts';

import { ApiError, apiRequest, isAbortError } from './api-client';
import { hasSession } from './token-store';

export interface SubscriptionState {
  /** `null` = 没有订阅（free 档用户），**不是**"还没加载出来"。 */
  subscription: Subscription | null;
  /** 是否成功拿到过一次结果。`false` 时 UI 不该得出"没有订阅"的结论。 */
  hydrated: boolean;
  pending: boolean;
  error: string | null;
}

const INITIAL_STATE: SubscriptionState = {
  subscription: null,
  hydrated: false,
  pending: false,
  error: null,
};

const state = reactive<SubscriptionState>({ ...INITIAL_STATE });

/** 当前状态（只读语义，不要直接改；改状态请用本模块导出的函数）。 */
export function getSubscriptionState(): SubscriptionState {
  return state;
}

/** 服务端（或支付结果）返回了新的订阅快照时调用。 */
export function applySubscription(subscription: Subscription | null): void {
  state.subscription = subscription;
  state.hydrated = true;
  state.error = null;
}

/**
 * 置为"没有订阅"。
 *
 * 登出、令牌失效时必须调用：否则下一个登录的账号会先看到上一个账号的档位与到期时间，
 * 这是**跨账号的信息泄漏**，比显示不出来严重得多。
 */
export function clearSubscription(): void {
  state.subscription = null;
  state.hydrated = false;
  state.pending = false;
  state.error = null;
}

let inFlight: Promise<Subscription | null> | null = null;

/**
 * 拉取当前订阅。
 *
 * 单飞：Header 与额度页会在同一帧里各自调一次，而它们要的是同一份数据。
 * 只发一个请求、共用同一个 Promise，也让 `pending` 的语义保持诚实。
 *
 * **没有令牌时直接返回 `null` 而不发请求**：`GET /subscription` 是受保护端点，
 * 未登录调用会拿到 40100，而 `api-client` 收到 40100 会广播 `unauthorized` 并
 * 清掉本地会话——"为了显示一个徽章把用户登出"是绝不能发生的事。
 */
export function loadSubscription(): Promise<Subscription | null> {
  if (!hasSession()) {
    return Promise.resolve(null);
  }
  if (inFlight === null) {
    inFlight = fetchSubscription().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function fetchSubscription(): Promise<Subscription | null> {
  state.pending = true;
  try {
    const result = await apiRequest('/subscription', SubscriptionResponseSchema, { method: 'GET' });
    applySubscription(result.subscription);
    return result.subscription;
  } catch (error) {
    // 拿不到新数据时**保留上一次的快照**：网络抖动不该把用户的档位徽章抹掉。
    // 令牌失效的场景由 api-client 广播 unauthorized → 上层 clearSubscription()，
    // 这里不重复处理，否则会出现"徽章消失但还登录着"的中间态。
    state.error =
      error instanceof ApiError
        ? error.message
        : isAbortError(error)
          ? '请求已取消'
          : '订阅信息加载失败';
    console.warn('[subscription-store] 订阅信息拉取失败，沿用上一次快照', error);
    return state.subscription;
  } finally {
    state.pending = false;
  }
}

/** 组合式入口：模板里直接 `const subscription = useSubscription()`。 */
export function useSubscription(): SubscriptionState {
  return state;
}

/** 仅测试用：把单例恢复成初始状态（含单飞中的 Promise）。 */
export function resetSubscriptionForTest(): void {
  inFlight = null;
  Object.assign(state, INITIAL_STATE);
}
